import { DetectedFoodItem, ReceiptMetadata } from '../types/foodRecognition';

/**
 * Multi-band ("tiled") receipt extraction support.
 *
 * A vision model spends a roughly fixed token budget on an image, so on a tall
 * grocery receipt each printed line ends up with very few tokens and small
 * characters get misread. Splitting the photo into a few overlapping horizontal
 * bands and extracting each one separately multiplies the attention spent on the
 * same pixels.
 *
 * Measured on the Costco Boucherville fixture (`npm run eval:receipt`):
 *   single pass  gemini-2.5-flash : 83.8% item recall, 79.5% quantity, ~93s
 *   4 bands      gemini-2.5-flash : 90.5% item recall, 92.7% quantity, ~47s
 * (6 bands scored worse than 4 — over-splitting costs the model context.)
 *
 * This module holds the pure merge logic so it can be unit-tested without a
 * network call; the banding/IO lives in imageProcessor + aiService.
 */

/** Bands the receipt is split into. 4 measured best; 6 over-splits. */
export const RECEIPT_BAND_COUNT = 4;

/** Fraction of each band that repeats the previous one, so no line is lost at a seam. */
export const RECEIPT_BAND_OVERLAP = 0.25;

export interface BandGeometry {
  /** Top edge of the band in source-image pixels. */
  top: number;
  /** Band height in source-image pixels. */
  height: number;
}

/**
 * Compute overlapping band rectangles covering an image of `imageHeight`.
 * Bands always stay inside the image and together cover it completely.
 */
export const computeBandGeometry = (
  imageHeight: number,
  bandCount: number = RECEIPT_BAND_COUNT,
  overlap: number = RECEIPT_BAND_OVERLAP,
): BandGeometry[] => {
  if (imageHeight <= 0 || bandCount <= 1) {
    return [{ top: 0, height: Math.max(0, imageHeight) }];
  }
  const bandHeight = Math.ceil(imageHeight / (bandCount - (bandCount - 1) * overlap));
  const step = Math.max(1, Math.round(bandHeight * (1 - overlap)));

  const bands: BandGeometry[] = [];
  for (let i = 0; i < bandCount; i++) {
    const top = Math.min(i * step, Math.max(0, imageHeight - bandHeight));
    const height = Math.min(bandHeight, imageHeight - top);
    if (height <= 0) break;
    // Skip a band that adds nothing beyond the previous one.
    const prev = bands[bands.length - 1];
    if (prev && prev.top === top) continue;
    bands.push({ top, height });
  }
  return bands.length > 0 ? bands : [{ top: 0, height: imageHeight }];
};

/**
 * Extra instructions appended to the standard receipt prompt when the model is
 * looking at one slice rather than the whole receipt.
 */
export const getBandPromptSuffix = (bandIndex: number, bandTotal: number): string => `

── THIS IMAGE IS SECTION ${bandIndex + 1} OF ${bandTotal} OF ONE TALL RECEIPT ──
You are seeing a horizontal slice, not the whole receipt. Adjacent sections overlap, so lines
near the top or bottom edge may also appear in a neighbouring section — that is expected.
• Extract ONLY the item lines fully visible in THIS slice. Never invent lines you cannot see.
• Skip the completeness/sum self-check: the printed total belongs to the whole receipt, not this slice.
• Always set receipt_line to the raw printed text INCLUDING the leading item code — it is what
  stitches the sections back together.
• If the store name, purchase date, or total is not visible in this slice, omit receipt_metadata.`;

/** Leading digits of a warehouse receipt line are the item code — the reliable join key. */
export const extractItemCode = (item: DetectedFoodItem): string | null => {
  const match = (item.receipt_line || '').trim().match(/^(\d{3,8})\b/);
  return match ? match[1] : null;
};

const normalizeName = (name: string): string =>
  name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => (w.length > 2 && w.endsWith('s') ? w.slice(0, -1) : w))
    .join(' ');

/**
 * Fallback key for receipts that do not print item codes (most supermarkets).
 * Name plus price identifies a physical line well enough to collapse the copy
 * seen twice across a seam.
 */
const looseKeyOf = (item: DetectedFoodItem): string => {
  const price = typeof item.total_price === 'number' ? item.total_price.toFixed(2) : '?';
  return `${normalizeName(item.name)}|${price}`;
};

/**
 * Merge the per-band extractions into one item list.
 *
 * Quantities are combined with MAX, not sum: a line that appears in two bands
 * because it sits in the overlap must not be double-counted, while a band that
 * genuinely saw two identical lines (e.g. bananas printed twice) already
 * reports quantity 2 and that larger value wins.
 */
export const mergeBandItems = (bandItemLists: DetectedFoodItem[][]): DetectedFoodItem[] =>
  stitchBandItems(bandItemLists).items;

/**
 * The product text of the printed line: the trailing price goes first, because
 * a band sometimes copies it into `receipt_line` and sometimes does not, then
 * spacing and punctuation. "HARICOTS VERTS UNICO 1,49" and "HARICOTS VERTS
 * UNICO" are one line; the price is compared on its own.
 */
const rawLineText = (item: ReceiptLineLike): string =>
  (item.receipt_line || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/(\s+-?\$?\d+[.,]\d{2}-?\$?(\s+[a-z]{1,2})?)+\s*$/, '')
    .replace(/[^a-z0-9]/g, '');

/** What the line comparisons read. A review row has these and little else. */
export type ReceiptLineLike = Pick<
  DetectedFoodItem,
  'name' | 'receipt_line' | 'total_price' | 'unit_price' | 'quantity'
>;

/** A raw line shorter than this identifies nothing ("LAIT" is every milk). */
const MIN_RAW_LINE = 6;
/** A word shorter than this is not evidence of a truncation ("lait" / "laitue"). */
const MIN_TRUNCATED_WORD = 5;

export const wordsOf = (name: string): string[] => normalizeName(name).split(' ').filter(Boolean);

/** "Unico Green Beans" and "Green Beans Unico": the same words, reshuffled. */
const isReorderingOf = (wordsA: string[], wordsB: string[]): boolean =>
  wordsA.length > 1 && [...wordsA].sort().join(' ') === [...wordsB].sort().join(' ');

/**
 * The prices a read may be quoting for its line: the line total, and the price
 * of one unit. A band that saw both tins reports 2 for 2,98; the next one saw a
 * single line at 1,49. Those agree.
 */
const candidatePrices = (item: ReceiptLineLike): number[] => {
  const out: number[] = [];
  if (typeof item.total_price === 'number') {
    out.push(item.total_price);
    if (typeof item.quantity === 'number' && item.quantity > 1) out.push(item.total_price / item.quantity);
  }
  if (typeof item.unit_price === 'number') out.push(item.unit_price);
  return out;
};

export const pricesAgree = (a: ReceiptLineLike, b: ReceiptLineLike): boolean => {
  const pricesA = candidatePrices(a);
  const pricesB = candidatePrices(b);
  if (pricesA.length === 0 || pricesB.length === 0) return true;
  return pricesA.some((x) => pricesB.some((y) => Math.abs(x - y) <= 0.011));
};

/**
 * Two reads of one name where a band cut a word short: "Pommes Sparta" and
 * "Pommes Spartan". Same number of words, each one equal or a prefix of the
 * other. A word carrying a digit must match exactly (1% is not 2%), and an
 * edit in the middle of a word is not a truncation (poire is not poivre).
 */
const isTruncationOf = (wordsA: string[], wordsB: string[]): boolean => {
  if (wordsA.length === 0 || wordsA.length !== wordsB.length) return false;
  return wordsA.every((wa, i) => {
    const wb = wordsB[i];
    if (wa === wb) return true;
    if (/\d/.test(wa) || /\d/.test(wb)) return false;
    const [short, long] = wa.length <= wb.length ? [wa, wb] : [wb, wa];
    return short.length >= MIN_TRUNCATED_WORD && long.startsWith(short);
  });
};

/**
 * Whether two code-less items from touching bands are one printed line.
 *
 * The exact key is the model's cleaned-up name, and each band cleans the line
 * up on its own: one wrote "Pommes Sparta", the next "Pommes Spartan", and the
 * cook was shown the same apples twice (#750). The printed text is the steadier
 * witness, so it is asked first; failing that, the names may differ only by a
 * truncation or by word order. Never against two prices that disagree: two
 * trays of chicken print the same text at different prices.
 */
export const isSameSeamLine = (a: ReceiptLineLike, b: ReceiptLineLike): boolean => {
  if (!pricesAgree(a, b)) return false;
  const rawA = rawLineText(a);
  if (rawA.length >= MIN_RAW_LINE && rawA === rawLineText(b)) return true;
  const wordsA = wordsOf(a.name);
  const wordsB = wordsOf(b.name);
  return isTruncationOf(wordsA, wordsB) || isReorderingOf(wordsA, wordsB);
};

/**
 * How much of a band may sit in the overlap, counted in items. The overlap is a
 * quarter of the band's height, but a band's items are not spread evenly over
 * it (the first carries the store header, the last the totals), so the bound is
 * generous. It only has to stop the top of one band meeting the bottom of the
 * next, which can never be the same line.
 */
const SEAM_SHARE = 2 / 3;

interface StitchedSlot {
  item: DetectedFoodItem;
  coded: boolean;
  /** Last band that contributed to this slot, and where in that band. */
  band: number;
  nearBandEnd: boolean;
}

export interface StitchedBands {
  items: DetectedFoodItem[];
  /** Lines joined by `isSameSeamLine` rather than by an identical key. */
  seamMerges: number;
}

/** `mergeBandItems`, plus how many seam lines needed the fuzzy join. */
export const stitchBandItems = (bandItemLists: DetectedFoodItem[][]): StitchedBands => {
  const byKey = new Map<string, StitchedSlot>();
  const order: string[] = [];
  let seamMerges = 0;

  bandItemLists.forEach((items, band) => {
    const list = items || [];
    list.forEach((item, index) => {
      const code = extractItemCode(item);
      const exactKey = code ? `code:${code}` : `loose:${looseKeyOf(item)}`;
      const nearBandStart = index < Math.ceil(list.length * SEAM_SHARE);
      const nearBandEnd = index >= Math.floor(list.length * (1 - SEAM_SHARE));

      let key = exactKey;
      if (!byKey.has(key) && !code && nearBandStart) {
        // One line pairs with one line: a slot this band already fed is taken.
        const seamKey = order.find((k) => {
          const slot = byKey.get(k)!;
          return !slot.coded && slot.band === band - 1 && slot.nearBandEnd && isSameSeamLine(slot.item, item);
        });
        if (seamKey) {
          key = seamKey;
          seamMerges += 1;
        }
      }

      const slot = byKey.get(key);
      if (!slot) {
        byKey.set(key, { item: { ...item }, coded: !!code, band, nearBandEnd });
        order.push(key);
        return;
      }
      // Same physical line seen from two bands — keep the more confident read,
      // and between equals the fuller name: the shorter one is the cut one.
      const existing = slot.item;
      const gap = (item.confidence || 0) - (existing.confidence || 0);
      const preferred = gap > 0 || (gap === 0 && item.name.length > existing.name.length) ? item : existing;
      const quantity = Math.max(existing.quantity ?? 1, item.quantity ?? 1);
      // The price travels with the quantity it was read for: 2 for 2,98 must
      // not become 2 for 1,49 because the single line was stitched in first.
      const larger = (item.quantity ?? 1) > (existing.quantity ?? 1) ? item : existing;
      const other = larger === item ? existing : item;
      byKey.set(key, {
        item: {
          ...preferred,
          quantity,
          unit: preferred.unit ?? existing.unit,
          total_price: larger.total_price ?? other.total_price,
        },
        coded: slot.coded,
        band,
        nearBandEnd,
      });
    });
  });

  return { items: order.map((k) => byKey.get(k)!.item), seamMerges };
};

/**
 * Combine per-band receipt metadata. Merges field by field so a band that
 * omitted the date cannot blank out the band that read it.
 */
export const mergeBandMetadata = (
  metadatas: Array<ReceiptMetadata | undefined>,
): ReceiptMetadata | undefined => {
  let merged: ReceiptMetadata | undefined;
  for (const meta of metadatas) {
    if (!meta) continue;
    merged = merged || {};
    for (const [key, value] of Object.entries(meta)) {
      // purchase_date_needs_review is not independent data — it describes a
      // specific band's date reading. Merging it on its own lets a band that
      // never saw the date (needs_review = true) override the band that read
      // it fine, so it is paired with purchase_date below instead.
      if (key === 'purchase_date_needs_review') continue;
      if (value === undefined || value === null || value === '') continue;
      if ((merged as Record<string, unknown>)[key] === undefined) {
        (merged as Record<string, unknown>)[key] = value;
      }
    }
  }
  if (!merged) return undefined;

  // Take the review flag from whichever band actually supplied the date.
  if (merged.purchase_date) {
    const source = metadatas.find((m) => m?.purchase_date === merged!.purchase_date);
    merged.purchase_date_needs_review = source?.purchase_date_needs_review ?? false;
  } else {
    merged.purchase_date_needs_review = true;
  }
  return merged;
};
